import { generatePath } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';

import { toIncidentCard } from './marketingOpsSelectors';

import type {
  WatchFinding,
  WatchFindingAffected,
  WatchNotificationLog,
  WatchTowerMockState,
} from './types';

export const AFFECTED_STATUSES = ['linked', 'reviewing', 'contacted', 'dismissed'] as const;

export type AffectedLinkStatus = typeof AFFECTED_STATUSES[number];

export const DIGEST_TO = 'marketing@attaima.example';

export interface OpportunityRow {
  link: WatchFindingAffected,
  finding: WatchFinding | null,
  sourceName: string,
  affectedName: string,
  productName: string | null,
}

export type RunDailyDigestResult =
  | { ok: true, state: WatchTowerMockState, log: WatchNotificationLog }
  | { ok: false, reason: 'empty' };

export function utcDay(iso: string): string {
  return iso.slice(0, 10);
}

export function companyNameById(state: WatchTowerMockState, id: number | null): string {
  if (!id) {
    return '—';
  }
  return state.watchCompanies.find((item) => item.id === id)?.name || String(id);
}

export function productNameById(
  state: WatchTowerMockState,
  companyId: number,
  productId: number | null,
): string | null {
  if (productId == null) {
    return null;
  }
  const company = state.watchCompanies.find((item) => item.id === companyId);
  return company?.products.find((item) => item.id === productId)?.name || String(productId);
}

export function findingByPublicId(
  state: WatchTowerMockState,
  publicId: string,
): WatchFinding | null {
  return state.findings.find((item) => item.publicId === publicId) || null;
}

export function opportunityRows(state: WatchTowerMockState): OpportunityRow[] {
  return state.findingAffectedCompanies.map((link) => {
    const finding = state.findings.find((item) => item.id === link.findingId) || null;
    const topic = finding?.watchTopicId
      ? (state.watchTopics || []).find((item) => item.id === finding.watchTopicId)
      : null;
    return {
      link,
      finding,
      sourceName: link.sourceWatchCompanyId
        ? companyNameById(state, link.sourceWatchCompanyId)
        : (topic?.title || '—'),
      affectedName: companyNameById(state, link.affectedWatchCompanyId),
      productName: productNameById(
        state,
        link.sourceWatchCompanyId || link.affectedWatchCompanyId,
        link.watchProductId,
      ),
    };
  });
}

export function setAffectedLinkStatus(
  state: WatchTowerMockState,
  linkId: number,
  status: string,
): WatchTowerMockState {
  return {
    ...state,
    findingAffectedCompanies: state.findingAffectedCompanies.map((link) => (
      link.id === linkId ? { ...link, status } : link
    )),
  };
}

export function logsForFinding(
  state: WatchTowerMockState,
  finding: WatchFinding,
): WatchNotificationLog[] {
  return state.notificationLog.filter((row) => (
    row.findingId === finding.id
    || (row.findingPublicIds || []).includes(finding.publicId)
  ));
}

export function findingStoryHref(publicId: string): string {
  return generatePath(AppRoutes.marketingOpsFinding, { publicId });
}

function isDailyDigest(row: WatchNotificationLog): boolean {
  return row.kind === 'daily_digest';
}

export function pendingDigestDay(state: WatchTowerMockState): string | null {
  const covered = new Set(
    state.notificationLog.filter(isDailyDigest).map((row) => utcDay(row.sentAt)),
  );
  const days = Array.from(new Set(
    state.findingAffectedCompanies
      .map((link) => link.notifiedAt)
      .filter((value): value is string => Boolean(value))
      .map(utcDay),
  )).sort();
  return [...days].reverse().find((day) => !covered.has(day)) || null;
}

function nextNotificationId(state: WatchTowerMockState): number {
  return state.notificationLog.reduce((max, row) => Math.max(max, row.id), 0) + 1;
}

export function runDailyDigest(
  state: WatchTowerMockState,
): RunDailyDigestResult {
  const day = pendingDigestDay(state);
  if (!day) {
    return { ok: false, reason: 'empty' };
  }

  const links = state.findingAffectedCompanies.filter((link) => (
    link.notifiedAt && utcDay(link.notifiedAt) === day
  ));
  if (!links.length) {
    return { ok: false, reason: 'empty' };
  }

  const findingIds = Array.from(new Set(links.map((link) => link.findingId)));
  const findings = findingIds
    .map((id) => state.findings.find((item) => item.id === id))
    .filter((item): item is WatchFinding => Boolean(item));
  const publicIds = findings.map((item) => item.publicId);
  const affectedIds = Array.from(new Set(links.map((link) => link.affectedWatchCompanyId)));

  const bodyLines = findings.map((finding) => {
    const topic = finding.watchTopicId
      ? (state.watchTopics || []).find((item) => item.id === finding.watchTopicId)
      : null;
    const source = topic?.title || companyNameById(state, finding.watchCompanyId);
    const names = links
      .filter((link) => link.findingId === finding.id)
      .map((link) => companyNameById(state, link.affectedWatchCompanyId));
    return `${finding.severity} · ${finding.title} · ${source} → ${names.join(', ')}`;
  });

  const log: WatchNotificationLog = {
    id: nextNotificationId(state),
    findingId: findings[0]?.id || links[0].findingId,
    to: DIGEST_TO,
    subject: `Daily Watch Tower opportunities (${day})`,
    appPath: AppRoutes.marketingOps,
    sentAt: `${day}T07:00:00.000Z`,
    affectedWatchCompanyIds: affectedIds,
    kind: 'daily_digest',
    findingPublicIds: publicIds,
    bodyPreview: bodyLines.join('\n'),
  };

  return {
    ok: true,
    log,
    state: {
      ...state,
      notificationLog: [...state.notificationLog, log],
    },
  };
}

export function storySourceCard(state: WatchTowerMockState, finding: WatchFinding) {
  return toIncidentCard(state, finding);
}
